import { useState } from "react";

import { faArrowLeft, faEye } from "@fortawesome/pro-regular-svg-icons";

import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Pill } from "src/base-components/Pill";
import { Button } from "src/design-system/Button";
import { IconButton } from "src/design-system/Icon";
import { Modal, useModal } from "src/design-system/Modal";
import { Tooltip } from "src/design-system/Tooltip";

export type SessionOverride = {
  userId: string;
  agentVersionId: string;
};

export const useSessionOverride = ({
  currentUserId,
  activeVersionId,
}: {
  currentUserId: string | undefined;
  activeVersionId: string | undefined;
}) => {
  const [viewingOverride, setViewingOverride] =
    useState<SessionOverride | null>(null);

  return {
    viewingOverride,
    setViewingOverride,
    effectiveUserId: viewingOverride?.userId ?? currentUserId,
    effectiveAgentVersionId: viewingOverride?.agentVersionId ?? activeVersionId,
    isViewingOtherSession: viewingOverride != null,
  };
};

type CopilotSessionViewerProps = {
  currentAgentVersionId: string | undefined;
  viewingOverride: SessionOverride | null;
  onSubmit: (override: SessionOverride | null) => void;
};

export const CopilotSessionViewer: React.FC<CopilotSessionViewerProps> = ({
  currentAgentVersionId,
  viewingOverride,
  onSubmit,
}) => {
  const { isOpen, openModal, closeModal } = useModal();
  const [userId, setUserId] = useState("");
  const [agentVersionId, setAgentVersionId] = useState("");

  const handleOpen = () => {
    setUserId(viewingOverride?.userId ?? "");
    setAgentVersionId(viewingOverride?.agentVersionId ?? "");
    openModal();
  };

  const handleSubmit = () => {
    const trimmedUserId = userId.trim();
    const trimmedVersionId = agentVersionId.trim();
    if (!trimmedUserId || !trimmedVersionId) return;

    onSubmit({ userId: trimmedUserId, agentVersionId: trimmedVersionId });
    closeModal();
  };

  return (
    <div className="flex items-center gap-x-0.5">
      {viewingOverride && (
        <Tooltip placement="top" title="Back to my session" asChild>
          <IconButton
            dataLoc="copilot-session-back"
            icon={faArrowLeft}
            size="xs"
            onClick={() => onSubmit(null)}
          />
        </Tooltip>
      )}
      <IconButton
        dataLoc="copilot-session-viewer-trigger"
        icon={faEye}
        size="xs"
        onClick={handleOpen}
      />
      <Modal open={isOpen} size="sm" onClose={closeModal}>
        <Modal.Header
          description={
            currentAgentVersionId && (
              <span className="inline-flex items-center gap-x-2 font-inter-normal-12px text-gray-500">
                Current agent version ID:
                <Pill fontType="code" size="sm" variant="gray">
                  <Pill.Text>{currentAgentVersionId}</Pill.Text>
                  <CopyTextIcon
                    feedback="inline"
                    size="2xs"
                    tooltip="Copy current version ID"
                    value={currentAgentVersionId}
                  />
                </Pill>
              </span>
            )
          }
        >
          View another user's session
        </Modal.Header>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Modal.Content>
            <FormItem gap="xs" label="User ID" isRequired>
              <Input
                placeholder="Enter user ID"
                value={userId}
                fullWidth
                onChange={(e) => setUserId(e.target.value)}
              />
            </FormItem>
            <FormItem gap="xs" label="Agent version ID" isRequired>
              <Input
                placeholder="Enter agent version ID"
                value={agentVersionId}
                fullWidth
                onChange={(e) => setAgentVersionId(e.target.value)}
              />
            </FormItem>
          </Modal.Content>
          <Modal.Footer
            primaryButton={
              <Button
                dataLoc="copilot-session-viewer-submit"
                isDisabled={!userId.trim() || !agentVersionId.trim()}
                htmlType="submit"
                variant="primary"
              >
                View session
              </Button>
            }
            secondaryButton="Cancel"
          />
        </form>
      </Modal>
    </div>
  );
};
